/** Oriente a recuperação sem encerrar a sessão ao abrir o QR. */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RotateCw, QrCode, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import EvolutionConnectionDialog from './EvolutionConnectionDialog';

interface Props {
  connectionNome?: string | null;
  instanceName: string;
  isLooping: boolean;
  loopCount: number;
  onRefresh: () => void;
}

type Phase = 'idle' | 'restarting' | 'verifying';

export default function EvolutionDownBanner({
  connectionNome,
  instanceName,
  isLooping,
  loopCount,
  onRefresh,
}: Props) {
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');

  const busy = phase !== 'idle';

  async function fetchState(): Promise<'open' | 'connecting' | 'close' | 'unknown'> {
    try {
      const { data } = await supabase.functions.invoke('evolution-instance-manage', {
        body: { action: 'connectionState', instance_name: instanceName },
      });
      const raw = data?.data?.instance?.state || data?.data?.state || 'unknown';
      if (raw === 'open' || raw === 'connecting' || raw === 'close') return raw;
      return 'unknown';
    } catch {
      return 'unknown';
    }
  }

  async function handleRestart() {
    setPhase('restarting');
    try {
      const { data, error } = await supabase.functions.invoke('evolution-instance-manage', {
        body: { action: 'restart', instance_name: instanceName },
      });
      if (error) throw error;
      if (data?.ok === false) {
        throw new Error(data?.error || data?.data?.message || `Falha ao reiniciar (HTTP ${data?.status})`);
      }
      setPhase('verifying');
      await new Promise((r) => setTimeout(r, 4000));
      let state = await fetchState();
      if (state === 'connecting') {
        await new Promise((r) => setTimeout(r, 4000));
        state = await fetchState();
      }
      onRefresh();
      if (state === 'open') {
        toast.success('Conexão restaurada! Pode mandar mensagem.');
      } else if (state === 'connecting') {
        toast.info('Ainda reconectando — aguarde alguns segundos.');
      } else if (state === 'close') {
        toast.warning(
          'A conexão ainda não voltou. Clique em "Reconectar WhatsApp" e siga as orientações com o celular da linha.',
          { duration: 6000 },
        );
      } else {
        toast.warning('Não consegui verificar o estado da conexão. Tente novamente.');
      }
    } catch (err: unknown) {
      toast.error(`Falha ao reiniciar: ${err instanceof Error ? err.message : 'Tente novamente.'}`);
    } finally {
      setPhase('idle');
    }
  }

  // ── UI ──
  const restartLabel = phase === 'restarting' ? 'Reiniciando…' : phase === 'verifying' ? 'Verificando…' : 'Tentar recuperar';

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 py-2.5 px-4 bg-primary/5 border-l-2 border-primary/60">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 text-primary dark:text-primary" />
        <div className="flex-1 min-w-0 text-xs leading-snug">
          {isLooping ? (
            <span className="text-foreground/80">
              <strong>{connectionNome || 'Conexão'}</strong> está instável. Clique em <strong>Reconectar WhatsApp</strong> para verificar a conexão e seguir as orientações.
            </span>
          ) : (
            <span className="text-foreground/80">
              <strong>{connectionNome || 'Conexão WhatsApp'}</strong> está desconectada. Tente recuperar a conexão ou reconecte com o celular da linha.
            </span>
          )}
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          {isLooping ? (
            // A consulta de QR preserva sessões ainda válidas.
            <Button
              size="sm"
              variant="outline"
              className="gap-1 h-7 text-xs border-primary/40 text-primary dark:text-primary hover:bg-primary/10 min-w-[140px]"
              onClick={() => setQrDialogOpen(true)}
              disabled={busy}
            >
              <QrCode className="h-3 w-3" /> Reconectar WhatsApp
            </Button>
          ) : (
            // Caída normal: tentar restart primeiro, depois QR
            <>
              <Button
                size="sm"
                variant="outline"
                className="gap-1 h-7 text-xs border-primary/40 text-primary dark:text-primary hover:bg-primary/10 min-w-[110px]"
                onClick={handleRestart}
                disabled={busy}
              >
                {phase === 'restarting' || phase === 'verifying' ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                {restartLabel}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="gap-1 h-7 text-xs text-muted-foreground hover:bg-muted"
                onClick={() => setQrDialogOpen(true)}
                disabled={busy}
              >
                <QrCode className="h-3 w-3" />
                Reconectar WhatsApp
              </Button>
            </>
          )}
        </div>
      </div>

      {qrDialogOpen && (
        <EvolutionConnectionDialog
          open={qrDialogOpen}
          onOpenChange={setQrDialogOpen}
          reconnectInstanceName={instanceName}
          onSuccess={() => {
            setQrDialogOpen(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
}
